import { useState, useEffect } from 'react';
import type { OverridesMap } from './DeveloperWorkbench.types';
import {
  checkDeveloperMode,
  getStoredOverrides,
  setStoredOverrides,
  clearStoredOverrides,
  reloadPage,
  MFE_CONFIGS,
} from './DeveloperWorkbench.utils';

/**
 * Custom hook managing the Developer Workbench state, visibility, and localStorage synchronization.
 */
export const useDeveloperWorkbench = () => {
  const [isDevMode, setIsDevMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [overrides, setOverrides] = useState<OverridesMap>({});

  // Initialize values on mount
  useEffect(() => {
    setIsDevMode(checkDeveloperMode());
    setOverrides(getStoredOverrides());
  }, []);

  /**
   * Updates a specific field of an MFE's override URLs in local state.
   */
  const handleInputChange = (mfeKey: string, field: 'scriptUrl' | 'cssUrl', value: string) => {
    setOverrides((prev) => {
      const current = prev[mfeKey] || { scriptUrl: '' };
      const updated = { ...current, [field]: value };

      // Clean up the object key if both fields are empty
      if (!updated.scriptUrl.trim() && !(updated.cssUrl || '').trim()) {
        const next = { ...prev };
        delete next[mfeKey];
        return next;
      }

      return {
        ...prev,
        [mfeKey]: {
          scriptUrl: updated.scriptUrl,
          ...(updated.cssUrl ? { cssUrl: updated.cssUrl } : {}),
        },
      };
    });
  };

  /**
   * Persists the overrides in localStorage and triggers a page reload to re-run MFE loaders.
   */
  const handleSave = () => {
    setStoredOverrides(overrides);
    setIsOpen(false);
    reloadPage();
  };

  /**
   * Clears all overrides, updates state, and reloads the page to default configurations.
   */
  const handleReset = () => {
    clearStoredOverrides();
    setOverrides({});
    setIsOpen(false);
    reloadPage();
  };

  return {
    isDevMode,
    isOpen,
    setIsOpen,
    overrides,
    handleInputChange,
    handleSave,
    handleReset,
    configs: MFE_CONFIGS,
  };
};
